import { useEffect, useState } from "react";
import { DatePicker, Drawer, Form, Input, Select } from "antd";
import dayjs from "dayjs";

import { AppButton } from "components/button/AppButton";
import {
  EMPLOYMENT_TYPES,
  JOB_POSTED_OPTIONS,
  JOB_STATUS_FILTER,
  LEVEL_LIST,
  WORK_MODELS,
  COUNTRIES,
} from "constants/general";
import { useGlobalContext } from "stateManagers/GlobalContext";
import { IdentifierProps } from "types";
import { useGetSectors } from "../hooks/useGetSectors";

const CITIES_API_URL = "https://countriesnow.space/api/v0.1/countries/cities";

export const FilterJob = ({ open, handleClose }: IdentifierProps) => {
  const { filterJobState, setFilterJobState } = useGlobalContext();
  const [form] = Form.useForm();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [filteredCities, setFilteredCities] = useState<any[]>([]);
  const [isFetchingCities, setIsFetchingCities] = useState(false);
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const { data: sectorData, isLoading: loadingSector } = useGetSectors();

  const isEmptyValue = (value: any): boolean => {
    if (value === undefined || value === null) return true;
    if (typeof value === "string" && value.trim() === "") return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  };

  const checkIfAllFieldsEmpty = () => {
    const values = form.getFieldsValue();

    const fieldsToCheck = [
      "title",
      "location_country",
      "location_city",
      "employment_type",
      "work_model",
      "closed",
      "level",
      "sector",
      "jobposted",
      "duration",
    ];

    const fieldStatus = fieldsToCheck.map((field) => {
      const value = values[field];
      const isEmpty = isEmptyValue(value);
      return isEmpty;
    });

    const allEmpty = fieldStatus.every((isEmpty) => isEmpty);
    return allEmpty;
  };

  useEffect(() => {
    if (open) {

      form.setFieldsValue({
        title: filterJobState.title || undefined,
        location_country: filterJobState.location_country || undefined,
        location_city: filterJobState.location_city || undefined,
        employment_type: filterJobState.employment_type || undefined,
        work_model: filterJobState.work_model || undefined,
        closed: filterJobState.closed || undefined,
        level: filterJobState.experience_level || undefined,
        sector: filterJobState.sector || undefined,
        jobposted: filterJobState.jobposted || undefined,
        duration:
          filterJobState.start_date && filterJobState.end_date
            ? [
              dayjs(filterJobState.start_date, "DD/MM/YYYY"),
              dayjs(filterJobState.end_date, "DD/MM/YYYY"),
            ]
            : undefined,
      });

      // ... rest of your code
    }
  }, [open, filterJobState]);

  const fetchCities = async (country: string) => {
    setIsFetchingCities(true);
    try {
      const response = await fetch(CITIES_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country }),
      });

      const data = await response.json();
      if (response.ok && Array.isArray(data.data)) {
        setFilteredCities(data.data);
      } else {
        console.error("Failed to fetch cities:", data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    } finally {
      setIsFetchingCities(false);
    }
  };

  const handleSubmit = (data: any) => {

    const payload: any = {};
    if (data.duration && data.duration.length === 2) {
      payload.start_date = data.duration[0].format("DD/MM/YYYY");
      payload.end_date = data.duration[1].format("DD/MM/YYYY");
    }

    const fieldMappings: Record<string, string> = {
      title: "title",
      location_country: "location_country",
      location_city: "location_city",
      employment_type: "employment_type",
      work_model: "work_model",
      closed: "closed",
      level: "experience_level",
      sector: "sector",
      jobposted: "jobposted",
    };

    Object.entries(fieldMappings).forEach(([formField, stateField]) => {
      const value = data[formField];
      if (value !== undefined && value !== null && value !== "") {
        payload[stateField] = value;
      }
    });


    setFilterJobState({
      ...filterJobState,
      ...payload,
    });

    handleClose();
  };

  const handleJobPostedFilter = (value: string, payload: any) => {
    const today = dayjs();
    let startDate: string;

    switch (value) {
      case "today":
        startDate = today.format("YYYY-MM-DD");
        payload.created_at_start = startDate;
        payload.created_at_end = today.format("YYYY-MM-DD");
        break;
      case "this_week":
        startDate = today.startOf("week").format("YYYY-MM-DD");
        payload.created_at_start = startDate;
        payload.created_at_end = today.format("YYYY-MM-DD");
        break;
      case "this_month":
        startDate = today.startOf("month").format("YYYY-MM-DD");
        payload.created_at_start = startDate;
        payload.created_at_end = today.format("YYYY-MM-DD");
        break;
      case "last_30_days":
        startDate = today.subtract(30, "days").format("YYYY-MM-DD");
        payload.created_at_start = startDate;
        payload.created_at_end = today.format("YYYY-MM-DD");
        break;
    }
  };

  const handleCountryChange = async (value: string | null) => {
    if (!value) {
      setSelectedCountry(null);
      setFilteredCities([]);
      form.setFieldsValue({ location_city: undefined });
      return;
    }

    setSelectedCountry(value);
    setFilteredCities([]);
    form.setFieldsValue({ location_city: undefined });

    await fetchCities(value);
  };

  const handleReset = () => {
    form.resetFields();
    setSelectedCountry(null);
    setFilteredCities([]);
    setFilterJobState({});
    setIsApplyDisabled(true);
  };

  const updateApplyButtonState = () => {
    const isEmpty = checkIfAllFieldsEmpty();
    setIsApplyDisabled(isEmpty);
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        handleClose();
      }}
      title="Filter Jobs"
      width={400}
    >
      <Form
        layout="vertical"
        onFinish={handleSubmit}
        form={form}
        onValuesChange={() => {
          updateApplyButtonState();
        }}
      >
        <Form.Item name="title" label="Job Title">
          <Input
            placeholder="e.g. Software Engineer"
            allowClear
            onChange={(e) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="location_country" label="Location (Country)">
          <Select
            allowClear
            placeholder="Select"
            showSearch
            options={COUNTRIES.map((country) => ({
              value: country.name,
              label: country.name,
            }))}
            onChange={(value) => {
              handleCountryChange(value);
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="location_city" label="Location (City)">
          <Select
            showSearch
            allowClear
            placeholder={
              !selectedCountry ? "Select country first" : "Select city"
            }
            disabled={!selectedCountry || isFetchingCities}
            loading={isFetchingCities}
            options={filteredCities.map((city) => ({
              value: city,
              label: city,
            }))}
            onChange={(value) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="employment_type" label="Job Type">
          <Select
            allowClear
            placeholder="Select"
            options={EMPLOYMENT_TYPES}
            onChange={(value) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="work_model" label="Work model">
          <Select
            allowClear
            placeholder="Select"
            options={WORK_MODELS}
            onChange={(value) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="closed" label="Job status">
          <Select
            allowClear
            placeholder="Select"
            options={JOB_STATUS_FILTER}
            onChange={(value) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="level" label="Experience Level">
          <Select
            allowClear
            placeholder="Select"
            options={LEVEL_LIST}
            onChange={(value) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="sector" label="Sector">
          <Select
            allowClear
            placeholder="Select"
            loading={loadingSector}
            filterOption={(input, option) => {
              return (
                option?.label?.toLowerCase().includes(input.toLowerCase()) ||
                false
              );
            }}
            options={sectorData?.map((sector) => ({
              value: sector.id,
              label: sector.name,
            }))}
            onChange={(value) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="jobposted" label="Date Range for Job Posted">
          <Select
            allowClear
            placeholder="Select"
            options={JOB_POSTED_OPTIONS}
            onChange={(value) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <Form.Item name="duration" label="Expire Date">
          <DatePicker.RangePicker
            className="w-full"
            format="DD/MM/YYYY"
            onChange={(dates, dateStrings) => {
              updateApplyButtonState();
            }}
          />
        </Form.Item>

        <div className="flex items-center justify-end gap-4 mt-6">
          <AppButton
            type="button"
            label="Reset"
            variant="transparent"
            handleClick={handleReset}
          />
          <button
            type="submit"
            disabled={isApplyDisabled}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${isApplyDisabled
                ? "bg-[#D3D7ED] text-gray-400 cursor-not-allowed border border-blue-500"
                : "bg-[#00094A] text-white hover:bg-blue-700 border border-blue-600"
              }`}
          >
            Apply
          </button>
        </div>
      </Form>
    </Drawer>
  );
};
